import { inject, Injectable, OnDestroy } from '@angular/core';
import { filter, Observable, Subject, takeUntil } from 'rxjs';
import { SignalRService } from '@k12/app-services';
import { SubTaskStatusUpdateMessage, SubTaskStatusUpdatePayload } from '@k12/models';

@Injectable({
  providedIn: 'root'
})
export class TaskStatusService implements OnDestroy {
  private readonly signalRService = inject(SignalRService);
  private readonly ngDestroy: Subject<void> = new Subject<void>();
  private readonly allTaskStatusUpdates: Subject<SubTaskStatusUpdatePayload> = new Subject<SubTaskStatusUpdatePayload>();

  constructor() {
    setTimeout(() => {
      this.registerSignalREvents();
    }, 100);
  }

  ngOnDestroy(): void {
    this.ngDestroy.next();
    this.ngDestroy.complete();
  }

  /**
   * Returns an observable filtered to only emit updates for the specified task and subtask.
   * @param taskId The ID of the task to filter by
   * @param subTaskId The ID of the subtask to filter by
   * @returns Observable that emits only matching SubTaskStatusUpdatePayload events
   */
  public getTaskStatusUpdates(taskId: string, subTaskId: string): Observable<SubTaskStatusUpdatePayload> {
    return this.allTaskStatusUpdates.asObservable().pipe(
      filter((payload) => {
        const taskMatches = payload.taskId?.toLowerCase() === taskId?.toLowerCase();
        const subTaskMatches = payload.subTaskId?.toLowerCase() === subTaskId?.toLowerCase();
        return taskMatches && subTaskMatches;
      })
    );
  }

  private registerSignalREvents(): void {
    this.signalRService.signalRConnectionStatus.pipe(takeUntil(this.ngDestroy)).subscribe((status) => {
      if (status === 'open') {
        this.attachSignalREvents();
      } else {
        this.detachSignalREvents();
      }
    });
  }

  private attachSignalREvents(): void {
    this.signalRService.hubConnection?.on('TaskStatusUpdated', (message) => {
      try {
        const msg: SubTaskStatusUpdateMessage =  JSON.parse(message);
        console.log('TaskStatusUpdated received:', msg);
        this.allTaskStatusUpdates.next(msg.body);
      } catch (error) {
        console.error('Error parsing TaskStatusUpdated message:', error, message);
      }
    });
  }

  private detachSignalREvents(): void {
    this.signalRService.hubConnection?.off('TaskStatusUpdated');
  }
}
